import Excel from 'exceljs';
import { DateTime } from 'luxon';

import Exception from '@/common/models/Exception';
import ExportFileType from '@/utils/ExportFileType';
import i18n from '@/vueI18n';

import { generateCSVURI } from './CsvUtils';

interface IBorder {
  style: string;
  color: { argb: string };
}

interface IBorders {
  top?: IBorder;
  bottom?: IBorder;
  left?: IBorder;
  right?: IBorder;
}

export enum ExportFormat {
  CSV = 'csv',
  XLSX = 'xlsx',
}

export enum CellType {
  BOOLEAN = 'boolean',
  PERCENTAGE = 'percentage',
  CURRENCY = 'currency',
  INTEGER = 'integer',
  DOUBLE = 'double',
  COORDINATE = 'coordinate',
  DATE_TIME = 'dateTime',
  DATE = 'date',
  NUMBER = 'number',
  STRING = 'string',
}

export function closeTheGaps(str): string {
  return str.replace(' ', '_');
}

export function validate(name: string, rows): void {
  if (!name) {
    throw new Exception('A name is mandatory');
  }

  if (!rows?.length) {
    throw new Exception('Cannot generate an empty file');
  }
}

export function getCellFormat(type: CellType): string {
  let numFmt = null;

  switch (type) {
    case CellType.BOOLEAN: {
      break;
    }
    case CellType.PERCENTAGE: {
      numFmt = '0.00%';
      break;
    }
    case CellType.CURRENCY: {
      let addSpaceBetweenNumberAndCurrency = false;
      let isCurrencySymbolLeading = true;
      let currencySymbol = '';

      const currencyString = i18n.global.n(123456789.0123456, 'currency');
      const numberMatches = currencyString.match(/(?:[^\W]+[0-9\s,.])+/g);
      if (numberMatches.length > 0) {
        addSpaceBetweenNumberAndCurrency = currencyString.indexOf(' ') > -1;
        isCurrencySymbolLeading = !currencyString.startsWith(numberMatches[0]);
        currencySymbol = currencyString.replace(' ', '').replace(numberMatches[0], '');
      }
      const prefix = `${isCurrencySymbolLeading ? currencySymbol : ''}${addSpaceBetweenNumberAndCurrency ? ' ' : ''}`;
      const suffix = `${addSpaceBetweenNumberAndCurrency ? ' ' : ''}${isCurrencySymbolLeading ? '' : currencySymbol}`;
      const decimals = currencySymbol === '¥' ? '' : '.00';
      numFmt = `${prefix}#,0${decimals}${suffix}`;
      break;
    }
    case CellType.INTEGER: {
      numFmt = '#,0';
      break;
    }
    case CellType.DOUBLE: {
      numFmt = '#,0.00';
      break;
    }
    case CellType.COORDINATE: {
      numFmt = '#,0.000000';
      break;
    }
    case CellType.DATE_TIME: {
      numFmt = 'yyyy-m-d h:mm:ss AM/PM';
      break;
    }
    case CellType.DATE: {
      numFmt = 'yyyy-mm-dd';
      break;
    }
    default:
      break;
  }

  return numFmt;
}

export function downloadAsCsv(
  name: string,
  rows: (string | number)[][],
  colTypes: CellType[],
  colNames: string[],
): void {
  validate(name, rows);

  const formattedRows = rows.map((row) =>
    row.map((cell, i) =>
      colTypes[i] === CellType.DATE_TIME &&
      typeof cell === CellType.NUMBER &&
      DateTime.fromMillis(cell as number).isValid
        ? DateTime.fromMillis(cell as number)
            .toUTC()
            .toISO()
        : `${cell}`,
    ),
  );

  const csvContent = generateCSVURI(colNames ? [colNames, ...formattedRows] : formattedRows);
  const link = document.createElement('a');
  link.setAttribute('href', csvContent);
  link.setAttribute('download', `${closeTheGaps(name)}.csv`);
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export function saveBlobToFile(blob: Blob, fileName: string): void {
  const a = document.createElement('a');
  document.body.appendChild(a);
  a.style.display = 'none';
  const url = window.URL.createObjectURL(blob);
  a.href = url;
  a.download = fileName;
  a.click();
  window.URL.revokeObjectURL(url);
}

export function downloadAsXlsx(
  name: string,
  rows: (string | number)[][],
  colTypes: CellType[],
  colNames: string[],
): void {
  validate(name, rows);
  const workbook = new Excel.Workbook();
  workbook.views = [
    {
      x: 0,
      y: 0,
      width: 30000,
      height: 20000,
      firstSheet: 0,
      activeTab: 1,
      visibility: 'visible',
    },
  ];

  const sheet = workbook.addWorksheet(name);
  if (colNames) sheet.addRow(colNames);

  // convert UNIX TS to Date JS (UTC) object so ExcelJS can use numFmt to format the value
  rows.forEach((row) =>
    sheet.addRow(
      row.map((r, i) => {
        if (
          colTypes[i] === CellType.DATE_TIME &&
          typeof r === CellType.NUMBER &&
          DateTime.fromMillis(r as number).isValid
        ) {
          const date = new Date(r);
          const utc = Date.UTC(
            date.getUTCFullYear(),
            date.getUTCMonth(),
            date.getUTCDate(),
            date.getUTCHours(),
            date.getUTCMinutes(),
            date.getUTCSeconds(),
          );

          return new Date(utc);
        }

        return r;
      }),
    ),
  );

  sheet.columns.forEach((column, i) => {
    // autoresize columns
    const lengths = column.values.map((v) => v.toString().length);

    column.width = CellType.DATE_TIME ? 18 : Math.max(...lengths.filter((v) => typeof v === 'number'));

    column.numFmt = getCellFormat(colTypes[i]);
  });

  workbook.xlsx.writeBuffer().then((data) => {
    const blob = new window.Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveBlobToFile(blob, `${closeTheGaps(name)}.xlsx`);
  });
}

export function downloadAsImg(name: string, base64Url: string): void {
  const downloadLink = document.createElement('a');
  document.body.appendChild(downloadLink);

  downloadLink.href = base64Url;
  downloadLink.target = '_self';
  downloadLink.download = closeTheGaps(name);
  downloadLink.click();
  downloadLink.remove();
}

export function setRangeBorder(sheet, rowStart, rowEnd, colStart, colEnd, color) {
  for (let r = rowStart; r <= rowEnd; r += 1) {
    for (let c = colStart; c <= colEnd; c += 1) {
      const border: IBorders = {};
      if (r === rowStart) border.top = { style: 'thin', color: { argb: color } };
      if (r === rowEnd) border.bottom = { style: 'thin', color: { argb: color } };
      if (c === colStart) border.left = { style: 'thin', color: { argb: color } };
      if (c === colEnd) border.right = { style: 'thin', color: { argb: color } };

      sheet.getCell(r, c).border = border;
    }
  }
}

export function formattedCellValue(fileType, str) {
  return fileType === ExportFileType.XLSX ? str : JSON.stringify(str);
}

const defaultFontSize = 14;

const workbookViewOptions = {
  x: 0,
  y: 0,
  width: 30000,
  height: 20000,
  firstSheet: 0,
  activeTab: 0,
  visibility: 'visible',
};

const colors = {
  red: 'FFff0083',
  green: 'FF00e074',
  darkGreen: 'FF00c767',
  blue: 'FF0F78FF',
  grey: 'FF888888',
};

const DownloadUtils = {
  downloadAsCsv,
  downloadAsXlsx,
  downloadAsImg,
  formattedCellValue,
  getCellFormat,
  saveBlobToFile,
  setRangeBorder,
  colors,
  defaultFontSize,
  workbookViewOptions,
};

export default DownloadUtils;
